import {
  type EventKind,
  FORM_KINDS,
  type FormAnswer,
  type FormKind,
  type FormStatus,
  type NewForm,
  type PermissionForm,
  addDays,
  familyStatus,
  formClosed,
  formError,
  rosterSummary,
  todayIso,
  useAbroadHome,
  useClasses,
  useFormActions,
  useFormAnswers,
  useFormRoster,
  useGuardianCount,
  usePermissionForms,
} from '@shkola/api';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { Spinner } from '../components/ui';
import {
  Block,
  DEEP,
  EventArt,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  TaskArt,
  Tabs,
  grad,
  pillClass,
  ringButton,
} from '../design/forms';
import {
  ArrowUpRightIcon,
  CaretDownIcon,
  CheckIcon,
  PaperPlaneTiltIcon,
  PlusIcon,
  XIcon,
} from '../design/icons';
import { buttonClass } from '../design/ui';

/*
 * «Згоди й форми» in «Форми 2» (28.10): the title on the paper, then each form as one white card — on
 * the left its face in a soft gradient of its kind (a trip, an excursion, an event) with the picture
 * big and quiet behind, the title, when; on the right what a parent needs to decide (where, how much,
 * the details, the deadline) and the answer with the signature — or, for the staff, who answered, the
 * bar of yes and no, the list and the reminder. One card under another: no half-empty columns.
 */

/** A form's kind: its picture and its gradient. */
const KIND_LOOK: Record<FormKind, { art: EventKind; tone: Grad }> = {
  excursion: { art: 'other', tone: 'apricot' },
  trip: { art: 'trip', tone: 'sky' },
  event: { art: 'celebration', tone: 'pink' },
  other: { art: 'meeting', tone: 'aqua' },
};

/** A family's answer as a colour. */
const STATUS_TONE: Record<FormStatus, Grad> = {
  agreed: 'mint',
  refused: 'coral',
  waiting: 'butter',
  partly: 'apricot',
  abroad: 'sky',
};

/** A bar's fill: the deep pair of a colour, so it reads on the paper. */
const bar = (tone: Grad) => `linear-gradient(90deg, ${DEEP[tone][0]}, ${DEEP[tone][1]})`;

const FIELD =
  'w-full rounded-[14px] bg-white px-3.5 text-[14px] text-ink outline-none ring-1 ring-[#E4E0D7] ring-inset transition-shadow placeholder:text-faint focus:ring-2 focus:ring-[var(--color-primary)]';

/** «12 листопада» */
const dayMonth = (iso: string) =>
  new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', day: 'numeric', month: 'long' }).format(
    new Date(`${iso}T00:00:00Z`),
  );

const timeStamp = (iso: string) =>
  new Intl.DateTimeFormat('uk-UA', {
    timeZone: 'Europe/Kyiv',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));

const whenOf = (f: PermissionForm) =>
  `${dayMonth(f.startsOn)}${f.endsOn !== f.startsOn ? ` – ${dayMonth(f.endsOn)}` : ''}${f.atTime ? `, ${f.atTime}` : ''}`;

/** A label: a soft rectangle in a colour (never a pill — a pill is a button). */
function Label({
  tone,
  white = false,
  children,
}: {
  tone: Grad;
  /** on a coloured card: white, the words in the colour's ink */
  white?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center gap-1 rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={
        white
          ? { backgroundColor: 'rgb(255 255 255 / 0.8)', color: GRAD[tone].ink }
          : { backgroundImage: grad(tone), color: GRAD[tone].ink }
      }
    >
      {children}
    </span>
  );
}

function StatusLabel({ status, white = false }: { status: FormStatus; white?: boolean }) {
  const { t } = useTranslation();
  return (
    <Label tone={STATUS_TONE[status]} white={white}>
      {t(`forms.status.${status}`)}
    </Label>
  );
}

/** The face of a form: its kind's gradient and picture, the title, when; labels on top. */
function Face({ form, top }: { form: PermissionForm; top: ReactNode }) {
  const { t } = useTranslation();
  const closed = formClosed(form, todayIso());
  const look = KIND_LOOK[form.kind];
  const tone: Grad = closed ? 'stone' : look.tone;
  return (
    <div
      className="relative isolate flex min-h-[208px] min-w-0 flex-col overflow-hidden rounded-[22px] p-5"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <EventArt
        kind={look.art}
        tone={tone}
        size={168}
        ghost
        className="pointer-events-none absolute -top-5 -right-6 -z-10"
      />
      <div className="flex flex-wrap items-center gap-1.5">{top}</div>
      <p className="mt-auto pt-8 text-[13px] font-medium">{t(`forms.kind.${form.kind}`)}</p>
      <h2
        className="font-display text-[26px] leading-[31px] font-light tracking-[-0.03em]"
        style={{ color: INK }}
      >
        {form.title}
      </h2>
      <p className="mt-2 text-[13.5px]">{whenOf(form)}</p>
    </div>
  );
}

/** Where, how much, the details, the deadline — what a parent needs to decide. */
function Facts({ form }: { form: PermissionForm }) {
  const { t } = useTranslation();
  const closed = formClosed(form, todayIso());
  return (
    <div className="flex flex-col gap-3">
      {(form.place || form.cost) && (
        <dl className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[14px] leading-5">
          {form.place && (
            <>
              <dt style={{ color: INK_SOFT }}>{t('forms.place')}</dt>
              <dd style={{ color: INK }}>{form.place}</dd>
            </>
          )}
          {form.cost && (
            <>
              <dt style={{ color: INK_SOFT }}>{t('forms.cost')}</dt>
              <dd style={{ color: INK }}>{form.cost}</dd>
            </>
          )}
        </dl>
      )}
      {form.details && (
        <p className="text-[14px] leading-[21px] whitespace-pre-line" style={{ color: INK_SOFT }}>
          {form.details}
        </p>
      )}
      <p className="flex flex-wrap gap-1.5">
        <Label tone={closed ? 'stone' : 'butter'}>
          {closed
            ? t('forms.closed', { date: dayMonth(form.deadline) })
            : t('forms.deadline', { date: dayMonth(form.deadline) })}
        </Label>
        {form.bothGuardians && <Label tone="lavender">{t('forms.both')}</Label>}
      </p>
    </div>
  );
}

/** One form: the face on the left, what to read and do on the right, a list across under both. */
function FormCard({
  face,
  under,
  children,
}: {
  face: ReactNode;
  under?: ReactNode;
  children: ReactNode;
}) {
  return (
    <article className="grid min-w-0 gap-4 rounded-[28px] bg-white p-3 md:p-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-6">
      {face}
      <div className="flex min-w-0 flex-col gap-4 px-2 pb-2 lg:px-0 lg:py-2 lg:pr-3">
        {children}
      </div>
      {under && <div className="min-w-0 lg:col-span-2">{under}</div>}
    </article>
  );
}

// ---------------------------------------------------------------------------------------------
// The family
// ---------------------------------------------------------------------------------------------

function AnswerBox({
  form,
  studentId,
  mine,
  onDone,
}: {
  form: PermissionForm;
  studentId: string;
  mine?: FormAnswer;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const actions = useFormActions(ctx.schoolId);
  const [name, setName] = useState(mine?.signedName ?? '');
  const [note, setNote] = useState(mine?.note ?? '');
  const send = (agreed: boolean) =>
    void actions.answer
      .mutateAsync({ formId: form.id, studentId, agreed, signedName: name, note })
      .then(onDone, () => undefined);
  const ready = name.trim().length >= 2 && !actions.answer.isPending;
  return (
    <div className="flex flex-col gap-3 rounded-[22px] p-4" style={{ backgroundColor: PAPER }}>
      {form.notePrompt && (
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium" style={{ color: INK_SOFT }}>
            {t('forms.note')}: {form.notePrompt}
          </span>
          <textarea
            rows={2}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={`${FIELD} py-2.5`}
          />
        </label>
      )}
      <label className="block">
        <span className="mb-1.5 block text-[13px] font-medium" style={{ color: INK_SOFT }}>
          {t('forms.sign')} — {t('forms.signHint')}
        </span>
        <input
          value={name}
          maxLength={200}
          placeholder={t('forms.signPlaceholder')}
          onChange={(e) => setName(e.target.value)}
          className={`${FIELD} h-11`}
        />
      </label>
      {actions.answer.isError && (
        <p role="alert" className="text-[13.5px]" style={{ color: GRAD.coral.ink }}>
          {t(`forms.errors.${formError(actions.answer.error)}`)}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!ready}
          onClick={() => send(true)}
          className={`${pillClass('dark')} disabled:cursor-not-allowed disabled:opacity-40`}
        >
          {t('forms.agree')}
          <PillIcon icon={CheckIcon} />
        </button>
        <button
          type="button"
          disabled={!ready}
          onClick={() => send(false)}
          className={`${pillClass('light')} disabled:cursor-not-allowed disabled:opacity-40`}
        >
          {t('forms.disagree')}
          <PillIcon icon={XIcon} />
        </button>
      </div>
    </div>
  );
}

function FamilyCard({
  form,
  studentId,
  answers,
  guardians,
  abroad,
}: {
  form: PermissionForm;
  studentId: string;
  answers: FormAnswer[];
  guardians: number;
  abroad: boolean;
}) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const [changing, setChanging] = useState(false);
  const status = abroad ? 'abroad' : familyStatus(answers, form.bothGuardians, guardians);
  const mine = answers.find((a) => a.guardianId === ctx.memberId);
  const open = !formClosed(form, todayIso());
  const canAnswer = ctx.kind === 'guardian' && open && !abroad;
  return (
    <FormCard face={<Face form={form} top={<StatusLabel status={status} white />} />}>
      <Facts form={form} />
      {answers.length > 0 && (
        <ul className="flex flex-col" style={{ borderTop: `1px solid ${LINE}` }}>
          {answers.map((a) => (
            <li
              key={a.guardianId}
              className="flex items-start gap-2.5 py-2.5 text-[14px] leading-5"
              style={{ borderBottom: `1px solid ${LINE}`, color: INK }}
            >
              <span
                className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full"
                style={{
                  backgroundImage: grad(a.agreed ? 'mint' : 'coral'),
                  color: GRAD[a.agreed ? 'mint' : 'coral'].ink,
                }}
              >
                {a.agreed ? (
                  <CheckIcon size={11} weight="bold" aria-hidden />
                ) : (
                  <XIcon size={11} weight="bold" aria-hidden />
                )}
              </span>
              <span className="min-w-0 flex-1">
                {a.guardianId === ctx.memberId
                  ? t('forms.yourAnswer', {
                      answer: t(a.agreed ? 'forms.agree' : 'forms.disagree'),
                      name: a.signedName,
                      time: timeStamp(a.answeredAt),
                    })
                  : t('forms.otherAnswer', {
                      name: a.signedName,
                      answer: t(a.agreed ? 'forms.agree' : 'forms.disagree'),
                    })}
                {a.note && (
                  <span className="block text-[12.5px]" style={{ color: INK_SOFT }}>
                    «{a.note}»
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {ctx.kind === 'student' && status === 'waiting' && (
        <p className="text-[14px]" style={{ color: INK_SOFT }}>
          {t('forms.studentWaiting')}
        </p>
      )}
      {canAnswer &&
        (!mine || changing ? (
          <AnswerBox
            form={form}
            studentId={studentId}
            {...(mine && { mine })}
            onDone={() => setChanging(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setChanging(true)}
            className={`${buttonClass('outline')} self-start`}
          >
            {t('forms.change')}
          </button>
        ))}
    </FormCard>
  );
}

function FamilyForms({ forms }: { forms: PermissionForm[] }) {
  const ctx = useCurrentContext();
  const child = ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child : undefined;
  const studentId = child?.memberId ?? '';
  const answers = useFormAnswers(studentId).data ?? [];
  const guardians = useGuardianCount(studentId).data ?? 1;
  const abroad = Boolean(useAbroadHome(studentId).data);
  const today = todayIso();
  const mineFirst = forms
    .filter((f) => f.classId === child?.class?.id)
    .sort((a, b) => Number(formClosed(a, today)) - Number(formClosed(b, today)));
  return (
    <div className="flex flex-col gap-4">
      {mineFirst.map((f) => (
        <FamilyCard
          key={f.id}
          form={f}
          studentId={studentId}
          answers={answers.filter((a) => a.formId === f.id)}
          guardians={guardians}
          abroad={abroad}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The staff
// ---------------------------------------------------------------------------------------------

function Field({
  label,
  wide = false,
  children,
}: {
  label: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`block min-w-0 ${wide ? 'md:col-span-2' : ''}`}>
      <span className="mb-1.5 block text-[13px] font-medium" style={{ color: INK_SOFT }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function NewFormEditor({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const actions = useFormActions(ctx.schoolId);
  const classes = useClasses(ctx.schoolId).data ?? [];
  const homeroom = ctx.kind === 'teacher' ? ctx.homeroomClass : undefined;
  const start = addDays(todayIso(), 14);
  const [f, setF] = useState<NewForm>({
    classId: homeroom?.id ?? '',
    kind: 'excursion',
    title: '',
    details: '',
    place: '',
    startsOn: start,
    endsOn: start,
    atTime: '09:00',
    cost: '',
    deadline: addDays(start, -3),
    bothGuardians: false,
    notePrompt: '',
  });
  const set = (patch: Partial<NewForm>) => setF({ ...f, ...patch });
  const ok =
    f.classId && f.title.trim() && f.deadline <= (f.endsOn > f.startsOn ? f.endsOn : f.startsOn);
  const look = KIND_LOOK[f.kind];
  return (
    <Block className="gap-5">
      <div className="flex flex-wrap items-center gap-4">
        <h2
          className="min-w-0 flex-1 font-display text-[30px] leading-9 font-light tracking-[-0.035em]"
          style={{ color: INK }}
        >
          {t('forms.newForm')}
        </h2>
        <span
          className="relative isolate flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-[18px]"
          style={{ backgroundImage: grad(look.tone) }}
          aria-hidden
        >
          <EventArt kind={look.art} tone={look.tone} size={44} />
        </span>
      </div>
      <div className="overflow-x-auto">
        <div className="inline-flex">
          <Tabs<FormKind>
            value={f.kind}
            label={t('forms.newForm')}
            options={FORM_KINDS.map((k) => ({ id: k, label: t(`forms.kind.${k}`) }))}
            onChange={(kind) => set({ kind })}
          />
        </div>
      </div>
      <div className="grid gap-x-4 gap-y-3.5 md:grid-cols-2">
        {!homeroom && (
          <Field label={t('forms.class')}>
            <select
              className={`${FIELD} h-11`}
              value={f.classId}
              onChange={(e) => set({ classId: e.target.value })}
            >
              <option value="">—</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label={t('forms.formTitle')} wide>
          <input
            className={`${FIELD} h-11`}
            value={f.title}
            maxLength={200}
            placeholder={t('forms.formTitlePlaceholder')}
            onChange={(e) => set({ title: e.target.value })}
          />
        </Field>
        <Field label={t('forms.startsOn')}>
          <input
            type="date"
            className={`${FIELD} h-11`}
            value={f.startsOn}
            onChange={(e) =>
              set({ startsOn: e.target.value, deadline: addDays(e.target.value, -3) })
            }
          />
        </Field>
        <Field label={t('forms.endsOn')}>
          <input
            type="date"
            className={`${FIELD} h-11`}
            value={f.endsOn}
            onChange={(e) => set({ endsOn: e.target.value })}
          />
        </Field>
        <Field label={t('forms.atTime')}>
          <input
            type="time"
            className={`${FIELD} h-11`}
            value={f.atTime}
            onChange={(e) => set({ atTime: e.target.value })}
          />
        </Field>
        <Field label={t('forms.place')}>
          <input
            className={`${FIELD} h-11`}
            value={f.place}
            maxLength={200}
            onChange={(e) => set({ place: e.target.value })}
          />
        </Field>
        <Field label={t('forms.cost')}>
          <input
            className={`${FIELD} h-11`}
            value={f.cost}
            maxLength={200}
            onChange={(e) => set({ cost: e.target.value })}
          />
        </Field>
        <Field label={t('forms.deadline', { date: '' })}>
          <input
            type="date"
            className={`${FIELD} h-11`}
            value={f.deadline}
            onChange={(e) => set({ deadline: e.target.value })}
          />
        </Field>
        <Field label={t('forms.details')} wide>
          <textarea
            rows={3}
            maxLength={3000}
            className={`${FIELD} py-2.5`}
            value={f.details}
            placeholder={t('forms.detailsPlaceholder')}
            onChange={(e) => set({ details: e.target.value })}
          />
        </Field>
        <Field label={t('forms.notePrompt')} wide>
          <input
            className={`${FIELD} h-11`}
            value={f.notePrompt}
            maxLength={200}
            placeholder={t('forms.notePromptPlaceholder')}
            onChange={(e) => set({ notePrompt: e.target.value })}
          />
        </Field>
        <label className="flex items-center gap-2.5 md:col-span-2">
          <input
            type="checkbox"
            checked={f.bothGuardians}
            onChange={(e) => set({ bothGuardians: e.target.checked })}
            className="size-4 accent-[var(--color-primary)]"
          />
          <span className="text-[14px]" style={{ color: INK }}>
            {t('forms.both')}
          </span>
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!ok || actions.create.isPending}
          onClick={() =>
            void actions.create
              .mutateAsync({ ...f, authorId: ctx.memberId })
              .then(onDone, () => undefined)
          }
          className={`${pillClass('dark')} disabled:cursor-not-allowed disabled:opacity-40`}
        >
          {t('forms.create')}
          <PillIcon icon={PaperPlaneTiltIcon} />
        </button>
        <button type="button" onClick={onDone} className={buttonClass('outline', 'lg')}>
          {t('common.cancel')}
        </button>
      </div>
    </Block>
  );
}

function StaffCard({
  form,
  classLabel,
  canManage,
}: {
  form: PermissionForm;
  classLabel: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const roster = useFormRoster(form).data ?? [];
  const actions = useFormActions(ctx.schoolId);
  const [open, setOpen] = useState(false);
  const [reminded, setReminded] = useState<number>();
  const sum = rosterSummary(roster);
  const closed = formClosed(form, todayIso());
  const asked = Math.max(1, roster.length - sum.abroad);
  return (
    <FormCard
      face={
        <Face
          form={form}
          top={
            <span
              className="inline-flex h-6 items-center rounded-[7px] bg-white/80 px-2 text-[12px] leading-none font-medium"
              style={{ color: INK }}
            >
              {classLabel}
            </span>
          }
        />
      }
      under={
        open && (
          <ul className="rounded-[22px] px-4" style={{ backgroundColor: PAPER }}>
            {roster.map((r, i) => (
              <li
                key={r.studentId}
                className="flex flex-wrap items-start gap-x-3 gap-y-1 py-3"
                style={i > 0 ? { borderTop: `1px solid ${LINE}` } : undefined}
              >
                <span
                  className="min-w-40 flex-1 text-[14px] leading-5 font-medium"
                  style={{ color: INK }}
                >
                  {r.lastName} {r.firstName}
                </span>
                <span className="min-w-0 flex-[2] text-[12.5px]" style={{ color: INK_SOFT }}>
                  {r.answers.map((a) => (
                    <span key={a.at} className="flex items-center gap-1.5">
                      {a.agreed ? (
                        <CheckIcon size={11} weight="bold" aria-hidden color={GRAD.mint.ink} />
                      ) : (
                        <XIcon size={11} weight="bold" aria-hidden color={GRAD.coral.ink} />
                      )}
                      <span className="min-w-0">
                        {a.signedName} · {timeStamp(a.at)}
                        {a.note && <span style={{ color: INK }}> · «{a.note}»</span>}
                      </span>
                    </span>
                  ))}
                </span>
                <StatusLabel status={r.status} />
              </li>
            ))}
          </ul>
        )
      }
    >
      <Facts form={form} />
      <div className="flex flex-col gap-2">
        <p className="text-[15px] font-medium" style={{ color: INK }}>
          {t('forms.summary', sum)}
        </p>
        <span className="flex h-2 overflow-hidden rounded-full" style={{ backgroundColor: PAPER }}>
          <span style={{ width: `${(100 * sum.agreed) / asked}%`, backgroundImage: bar('mint') }} />
          <span
            style={{ width: `${(100 * sum.refused) / asked}%`, backgroundImage: bar('coral') }}
          />
        </span>
        {sum.abroad > 0 && (
          <p className="text-[12.5px]" style={{ color: INK_SOFT }}>
            {t('forms.abroadNote', { count: sum.abroad })}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className={pillClass('light', 'sm')}
        >
          {open ? t('forms.hideRoster') : t('forms.roster')}
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full ring-[1.5px] ring-current ring-inset">
            <CaretDownIcon
              size={12}
              weight="bold"
              aria-hidden
              className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
            />
          </span>
        </button>
        {canManage && !closed && sum.waiting > 0 && (
          <button
            type="button"
            disabled={actions.remind.isPending}
            onClick={() =>
              void actions.remind.mutateAsync(form.id).then(
                (n) => setReminded(n),
                () => undefined,
              )
            }
            className={`${pillClass('dark', 'sm')} disabled:opacity-40`}
          >
            {t('forms.remind')} ({sum.waiting})
            <PillIcon icon={PaperPlaneTiltIcon} size="sm" />
          </button>
        )}
        {canManage && (
          <button
            type="button"
            onClick={() => actions.remove.mutate(form.id)}
            className="ml-auto h-9 rounded-full px-3 text-[13px] font-medium underline-offset-2 hover:underline"
            style={{ color: INK_FAINT }}
          >
            {t('forms.remove')}
          </button>
        )}
      </div>
      {reminded !== undefined && (
        <p className="flex items-center gap-1.5 text-[13.5px]" style={{ color: GRAD.mint.ink }}>
          <CheckIcon size={13} weight="bold" aria-hidden />
          {reminded > 0 ? t('forms.reminded', { count: reminded }) : t('forms.remindedNone')}
        </p>
      )}
    </FormCard>
  );
}

function StaffForms({ forms }: { forms: PermissionForm[] }) {
  const ctx = useCurrentContext();
  const classes = useClasses(ctx.schoolId).data ?? [];
  const label = new Map(classes.map((c) => [c.id, c.label]));
  const homeroom = ctx.kind === 'teacher' ? ctx.homeroomClass : undefined;
  return (
    <div className="flex flex-col gap-4">
      {forms.map((f) => (
        <StaffCard
          key={f.id}
          form={f}
          classLabel={label.get(f.classId) ?? ''}
          canManage={ctx.kind === 'admin' || f.classId === homeroom?.id}
        />
      ))}
    </div>
  );
}

/** Nothing yet: a quiet card with its picture. */
function Empty({ children }: { children: ReactNode }) {
  return (
    <Block className="relative isolate min-h-[140px] justify-center overflow-hidden">
      <TaskArt
        kind="done"
        tone="lime"
        size={140}
        ghost
        className="pointer-events-none absolute -top-3 right-6 -z-10"
      />
      <p className="text-[16px]" style={{ color: INK_SOFT }}>
        {children}
      </p>
    </Block>
  );
}

/** «Згоди й форми»: trips and excursions agreed in the app, with the parents' signature. */
export function FormsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const forms = usePermissionForms(ctx.schoolId);
  const family = ctx.kind === 'student' || ctx.kind === 'guardian';
  const homeroom = ctx.kind === 'teacher' ? ctx.homeroomClass : undefined;
  const canCreate = !family && (ctx.kind === 'admin' || Boolean(homeroom));
  const [creating, setCreating] = useState(false);
  const list = forms.data ?? [];
  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('forms.title')}
        sub={
          <span className="block max-w-2xl">
            {family ? t('forms.intro') : t('forms.introStaff')}
          </span>
        }
      >
        {canCreate && !creating && (
          <button type="button" onClick={() => setCreating(true)} className={pillClass('dark')}>
            {t('forms.newForm')}
            <PillIcon icon={PlusIcon} />
          </button>
        )}
      </PageHead>
      {creating && <NewFormEditor onDone={() => setCreating(false)} />}
      {forms.isPending ? (
        <Spinner />
      ) : list.length === 0 ? (
        !creating && <Empty>{t('forms.empty')}</Empty>
      ) : family ? (
        <FamilyForms forms={list} />
      ) : (
        <StaffForms forms={list} />
      )}
    </div>
  );
}

/** On a guardian's home: forms of the child's class still waiting for this parent's signature. */
export function PendingFormsCard({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const child = ctx.kind === 'guardian' ? ctx.child : undefined;
  const forms = usePermissionForms(ctx.schoolId).data ?? [];
  const answers = useFormAnswers(child?.memberId ?? '').data ?? [];
  const abroad = Boolean(useAbroadHome(child?.memberId ?? '').data);
  const today = todayIso();
  const pending = forms.filter(
    (f) =>
      f.classId === child?.class?.id &&
      !formClosed(f, today) &&
      !answers.some((a) => a.formId === f.id && a.guardianId === ctx.memberId) &&
      !answers.some((a) => a.formId === f.id && !a.agreed),
  );
  if (!child || abroad || pending.length === 0) return null;
  return (
    <Link
      to="/forms"
      className={`group relative isolate flex min-h-[112px] items-center gap-4 overflow-hidden rounded-[28px] p-5 transition-transform duration-300 ease-spring hover:-translate-y-0.5 md:p-6 ${className}`}
      style={{ backgroundImage: grad('pink'), color: GRAD.pink.ink }}
    >
      {/* to confirm: a megaphone, big and quiet behind the words (as «Підтвердити» on the student's home) */}
      <TaskArt
        kind="ack"
        tone="pink"
        size={140}
        ghost
        className="pointer-events-none absolute -top-4 right-14 -z-10"
      />
      <span className="min-w-0 flex-1">
        <span
          className="block font-display text-[24px] leading-[30px] font-light tracking-[-0.025em]"
          style={{ color: INK }}
        >
          {t('forms.card', { count: pending.length })}
        </span>
        <span className="mt-1 block truncate text-[13.5px]">
          {pending.map((f) => f.title).join(' · ')}
        </span>
      </span>
      <span className={`${ringButton} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}>
        <ArrowUpRightIcon size={16} weight="bold" aria-hidden />
      </span>
    </Link>
  );
}
